import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { io } from 'socket.io-client';
import { environment } from '../../environments/environment';
import { NightwebUtil, getDistanceParameterLabel, getDistanceParameterData } from '../utils';

export interface ProximityAlertMessage {
  title: string;
  body: string;
  sendAt?: string;
}

export interface AdvocateLogMessage {
  message: string;
  zone: string;
  receivedAt: Date;
}

export interface ThreatPairAlert {
  id: string;
  zone: string;
  timestampLabel: string;
  sortDate: string;
  distance: string;
  thresholdLabel?: string;
  survivorNotified: boolean;
  perpetratorNotified: boolean;
}

@Injectable({ providedIn: 'root' })
export class SimulationSocketService {
  private readonly socketBaseUrl = `${environment.notificationUrl.replace(/\/$/, '')}/watch`;
  private readonly advocateLogSocketUrl = `${environment.notificationUrl}`;

  constructor(private readonly nightwebUtil: NightwebUtil) {}

  listenForProximityAlerts(userId: string): Observable<ProximityAlertMessage> {
    return new Observable((subscriber) => {
      const socket = io(this.socketBaseUrl, {
        transports: ['polling', 'websocket'],
        reconnection: true,
        reconnectionDelay: 5000,
        reconnectionDelayMax: 30000,
        reconnectionAttempts: Infinity,
        timeout: 20000,
        auth: { userId },
        extraHeaders: { auth: userId },
      });

      const onProximityAlert = (args: unknown): void => {
        const data = Array.isArray(args) ? args[0] : args;
        const alert = data && typeof data === 'object' ? data as Record<string, unknown> : {};
        subscriber.next({
          title: typeof alert['title'] === 'string' && alert['title'] ? alert['title'] : 'Alert!!',
          body: typeof alert['body'] === 'string' ? alert['body'] : '',
          sendAt: typeof alert['sendAt'] === 'string' ? alert['sendAt'] : undefined,
        });
      };

      socket.on('proximity-alert', onProximityAlert);
      return () => {
        socket.off('proximity-alert', onProximityAlert);
        socket.disconnect();
      };
    });
  }

  listenForAdvocateLogs(threatPairId: string, distanceParameter: string = 'meter'): Observable<AdvocateLogMessage> {
    const user = this.nightwebUtil.decodeToken(this.nightwebUtil.getLoggedInUser() as string);
    if (!user) {
      return throwError(() => new Error('User not found'));
    }
    const userId = user.id;
    return new Observable((subscriber) => {
      const socket = io(this.advocateLogSocketUrl, {
        secure: false,
        reconnection: true,
        reconnectionAttempts: 5,
        extraHeaders: { auth: userId },
      });
      socket.on('connect', () => {
        console.log('connect:::');
        socket.emit('threat-pair-join', threatPairId);
      });

      const onAdvocateLog = (args: unknown): void => {
        const data = Array.isArray(args) ? args[0] : args;
        const log = data && typeof data === 'object' ? data.data as ThreatPairAlert : {zone: 'outside'};

        subscriber.next({
          message: this.formattedAdvocateLog(log as ThreatPairAlert, distanceParameter),
          zone: log.zone,
          receivedAt: new Date(),
        });
      };

      socket.on('advocate-logs', onAdvocateLog);
      return () => {
        socket.off('advocate-logs', onAdvocateLog);
        socket.emit('threat-pair-leave', threatPairId);
        socket.disconnect();
      };
    });
  }

  formattedAdvocateLog(alert: ThreatPairAlert, distanceParameter: string = 'meter'): string {
    const unit = getDistanceParameterLabel(distanceParameter);
    const distanceMeters = alert.distance ? Number(alert.distance) : 0;
    const distance = getDistanceParameterData(distanceMeters, distanceParameter);
    const thRaw = alert.thresholdLabel ? Number(alert.thresholdLabel) : NaN;
    return `${distance.toFixed(3)} ${unit} detected — Threshold breached (${thRaw} ${unit})`;
  }
}
